import { useContext, useMemo, useState } from "react";
import intersectionBy from "lodash/intersectionBy";
import unionBy from "lodash/unionBy";

import BottomIconHideWrapper from "@stadium/shared/src/components/bottom-icon-hide-wrapper";

import { FilterList } from "@components/store/sidebar/filter-listing";
import BsDropdownMultiSelect from "@stadium/ui/src/components/dropdown/bs-dropdown-multiselect";

import { pluralizeWord } from "@stadium/helpers/string";

import { InternalButton } from "@sharedComponents/buttons";

import {
  DropdownComponent,
  DropdownItem,
  DropdownToggle,
} from "@stadium/ui/src/components/bootstrap-dropdown";
import { DropdownContext } from "@stadium/ui/src/components/bootstrap-dropdown/context";

import FilterWrapper from "@components/store/products/listing-header/filter-wrapper";
import { ProductListingContext } from "@components/store/products/listing/listing-provider";
import PriceRangeSlider from "@components/store/products/listing-header/price-range-slider";

import { PRICE_FILTER_KEY } from "@constants/products/listing";

import style from "./filterDropdown.module.scss";
import { LISTING_SECTION_REF } from "@components/store/products/listing";

const FilterDropDown = ({ data, isFetchingProducts, priceData }) => {
  const { isPriceRangeFilterEnabled, totalProducts } = useContext(ProductListingContext);

  const renderPriceRangeSlider = () => {
    const { key, ...restPriceData } = priceData;
    return (
      <li key={key} className={style.priceList}>
        <div className={`${style.keyHeading} keyHeading`}>{restPriceData.label}</div>
        <PriceRangeSlider key={key} {...restPriceData} />
        {isPriceRangeFilterEnabled && (
          <div className={`${style.rangeResultText}`}>
            {totalProducts} {pluralizeWord("result", { count: totalProducts })} in range
          </div>
        )}
      </li>
    );
  };

  return (
    <div
      className={`form-dropdown dropdownFilter ${style.catalogFilter} ${style.filterDropdownMenuWrapper}`}
    >
      <BsDropdownMultiSelect
        id="product-filter"
        label="Filters"
        parentLabel="Filters"
        data={data.filter((item) => item.key !== PRICE_FILTER_KEY)}
        className={style.bsDropdown}
        dropDownClass={style.filterDropdownMenu}
        menuWrapper={style.filterDropdownMenuWrapper}
        keyHeadingClass={style.keyHeading}
        filterListWrapperClass={style.filterListWrapper}
        filterListClass={style.filterListWrap}
        filterItemClass={style.filterDropdownItem}
        disabled={isFetchingProducts}
      >
        {renderPriceRangeSlider()}
      </BsDropdownMultiSelect>
    </div>
  );
};

const MAX_ITEMS_FOR_VIEW_ALL_TO_APPEAR = 7;

const ResponsiveFilterList = (props) => {
  const [currentFilterOption, setCurrentFilterOption] = useState(null);

  const { totalFiltersApplied, setFilterToDefault } = useContext(ProductListingContext);

  let filtersAppliedText = totalFiltersApplied ? `(${totalFiltersApplied})` : "";

  const filterTitle = `Filters ${filtersAppliedText}`;

  const { data: rawData } = props;

  const renderTitle = () => {
    if (!currentFilterOption) {
      return filterTitle;
    }
    return (
      <>
        <span
          className={style.backIcon}
          onClick={(e) => {
            e.stopPropagation();
            setCurrentFilterOption(null);
          }}
        />
        {currentFilterOption.label}
      </>
    );
  };

  const data = useMemo(() => {
    // no change in option ordering required if user opens a filter in full view
    if (currentFilterOption) {
      return rawData;
    }
    return rawData.map((filterItem) => {
      // no sorting required for options under these conditions
      if (
        !Array.isArray(filterItem.selectedIds) ||
        filterItem.key === PRICE_FILTER_KEY ||
        filterItem.items?.length <= MAX_ITEMS_FOR_VIEW_ALL_TO_APPEAR
      ) {
        return filterItem;
      }
      const { items, selectedIds } = filterItem;

      const selectedItems = selectedIds.map((id) => ({ id }));

      // sort items with selected ids first
      const sortedItems = unionBy(intersectionBy(items, selectedItems, "id"), items, "id");
      return { ...filterItem, items: sortedItems };
    });
  }, [currentFilterOption, rawData]);

  // show only the filter selected by passing just that filter in the data array
  const filterData = useMemo(() => {
    if (currentFilterOption) {
      // set label empty in order to not show panel label - we're already showing the label as dropdown heading
      return [
        {
          ...data.find(({ key }) => key === currentFilterOption.key),
          defaultOpen: true,
          label: "",
        },
      ];
    }
    return data;
  }, [currentFilterOption, data]);

  const maxItemsToShow = currentFilterOption ? 0 : MAX_ITEMS_FOR_VIEW_ALL_TO_APPEAR; // this controls view all logic - 0 to disable, any other value to show view all button

  return (
    <DropdownComponent
      heading={<DropdownToggle className={style.filterHeaderLabel}>{renderTitle()}</DropdownToggle>}
      label={
        <DropdownToggle
          className={`${style.filterCtaToggle} dropdown-toggle ftDropdownToggle`}
          activeClassName={style.openDDClass}
          showDropdownIcon={false}
        >
          {filterTitle}
        </DropdownToggle>
      }
      type="bottom-drawer"
      className={style.restrictionDropdown}
      menuClassName={style.dropdownMenu}
      onToggle={(isOpen) => {
        setCurrentFilterOption(null);
        if (LISTING_SECTION_REF.current) {
          if (isOpen) {
            LISTING_SECTION_REF.current.style = "z-index:11";
            return;
          }
          LISTING_SECTION_REF.current.style = "";
        }
      }}
    >
      <DropdownContext.Consumer>
        {({ isDropdownOpen }) =>
          isDropdownOpen && (
            <BottomIconHideWrapper>
              <FilterList
                {...props}
                data={filterData}
                onViewAll={setCurrentFilterOption}
                title="" // do not show any title - dropdown has a title by default
                maxItemsToShow={maxItemsToShow}
              />
              {Boolean(totalFiltersApplied) && (
                <div className={style.mobileFilterFooterBtn}>
                  <DropdownItem className={style.button}>
                    <InternalButton styleType="primary-outline" onClick={setFilterToDefault}>
                      Clear All
                    </InternalButton>
                  </DropdownItem>
                  <DropdownItem className={style.button}>
                    <InternalButton>Apply</InternalButton>
                  </DropdownItem>
                </div>
              )}
            </BottomIconHideWrapper>
          )
        }
      </DropdownContext.Consumer>
    </DropdownComponent>
  );
};

const RestrictionFilterDropdown = ({ isMobileDevice }) => {
  if (isMobileDevice) {
    return <FilterWrapper Component={ResponsiveFilterList} />;
  }
  return <FilterWrapper Component={FilterDropDown} />;
};

export default RestrictionFilterDropdown;
